// Thin wrapper over the pancakestack Lambda Function URL.
// The AuthContext supplies the id_token; this module is stateless.
//
// All bodies are JSON. Errors normalize into ApiError{code, message, status}.

import { environment } from '../config/environment';

const BASE = environment.apiBaseUrl;

// Archive extensions the backend accepts as tar-of-transport uploads.
// Mirrors `allowedArchiveExts` in lambda-start-job/multipart.go — the
// two lists MUST stay in sync (see CLAUDE.md "backend/frontend hand-
// mirrored" convention). Exported so the drop-zone accept list, the
// bulk-upload hash-skip, and the multipart hash-skip all read from
// one place.
export const ARCHIVE_EXTS = [
  '.tar', '.tar.gz', '.tgz', '.tar.zst', '.tar.bz2', '.tar.xz', '.zip', '.rar',
] as const;

export const isArchiveFilename = (name: string): boolean => {
  const l = name.toLowerCase();
  return ARCHIVE_EXTS.some((e) => l.endsWith(e));
};

export class ApiError extends Error {
  code: string;
  status: number;
  // Full JSON body from the failed response, when the response was JSON.
  // Preserves structured error payloads (e.g. STORAGE_QUOTA_EXCEEDED's
  // {used, quota, requested, shortfall}) so callers can narrow the type
  // and render an inline UI without re-parsing the message string.
  payload?: unknown;
  constructor(status: number, code: string, message: string, payload?: unknown) {
    super(message);
    this.code = code;
    this.status = status;
    this.payload = payload;
  }
}

type FetchOpts = { token?: string | null; signal?: AbortSignal };

const request = async <T>(
  method: string,
  path: string,
  body: unknown,
  opts: FetchOpts = {},
): Promise<T> => {
  if (!BASE) throw new ApiError(0, 'NO_API_BASE_URL', 'REACT_APP_API_BASE_URL is not configured');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;
  const resp = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: opts.signal,
  });
  const text = await resp.text();
  const json = text ? safeJson(text) : null;
  if (!resp.ok) {
    const code = (json && (json as any).code) || `HTTP_${resp.status}`;
    const msg  = (json && (json as any).error) || text || resp.statusText;
    throw new ApiError(resp.status, code, msg, json);
  }
  return (json as T) ?? (undefined as unknown as T);
};

const safeJson = (t: string): unknown => {
  try { return JSON.parse(t); } catch { return null; }
};

// ---- Types (mirror Lambda contracts) ----

export type ScriptId = 'seestar' | 'seestar-advanced';

export interface CatalogParamDef {
  name: string;
  type: 'float' | 'int' | 'bool' | 'string';
  default: number | boolean | string;
  min?: number;
  max?: number;
  // step is the slider granularity. Falls back to a heuristic on the
  // client if the backend didn't specify one.
  step?: number;
  // Enum can be numeric (int params) or string (string params). Mixed
  // arrays aren't valid — the type field decides which the values are.
  enum?: (number | string)[];
  label?: string;
  description?: string;
  /** Display group. Wizard emits one heading per group; params
      sharing a group are guaranteed adjacent in the params[] array
      so consumers can walk the list linearly and split on change. */
  group?: string;
}
export interface CatalogEntry {
  id: ScriptId;
  title: string;
  summary: string;
  params: CatalogParamDef[];
  /** Un-rendered .ssf template (Go text/template syntax). Surfaced to
      the wizard's script-picker so seasoned users can see exactly what
      Siril will run. Optional — older backends won't return it. */
  rawTemplate?: string;
}
export interface InstanceOption {
  type: string;
  vcpu: number;
  ramGB: number;
  approxSpotHourly: number;
  recommendedFor: string;
}

export interface UploadInitReq {
  collectionId: string;
  filename: string;           // .tar / .tar.gz / .tgz / .tar.zst / .zip
  contentType?: string;
  // SHA-256 of the file bytes, lowercase hex. Required — backend uses
  // it to reject duplicates before we even start streaming parts.
  sha256: string;
  // Total file size in bytes. Required — backend storage-quota gate
  // uses this to reject uploads that would exceed the plan cap before
  // minting the multipart upload.
  sizeBytes: number;
}
export interface UploadInitResp {
  uploadId?: string;
  key?: string;               // S3 object key we're building
  partSize?: number;          // recommended part size in bytes
  // Backend returns skipped=true when a file with this checksum
  // already exists in the collection. Caller must not start parts.
  skipped?: boolean;
  reason?: string;            // "duplicate"
  duplicateOf?: string;       // existing filename in same collection
}
export interface UploadPartReq {
  uploadId: string;
  key: string;
  partNumber: number;         // 1-based
}
export interface UploadPartResp {
  url: string;                // presigned PUT URL for this part
}
export interface CompletedPart { partNumber: number; etag: string; }
export interface UploadCompleteReq {
  uploadId: string;
  key: string;
  parts: CompletedPart[];
}
export interface UploadCompleteResp {
  key?: string;
  s3Uri?: string;             // s3://bucket/key
  sizeBytes?: number;
  // Synthesized by multipartUpload.ts when initUpload returns skipped;
  // never sent by the backend directly for /upload/complete.
  skipped?: boolean;
  reason?: string;
  duplicateOf?: string;
}

export interface StartJobReq {
  collectionId: string;
  scriptId: ScriptId;
  // Optional free-form label for this stack (e.g. "30s 3x drizzle"). Trimmed
  // + length-capped server-side (see nameMaxLen in the Lambda). UI falls back
  // to collectionId when empty.
  name?: string;
  params?: Record<string, number | boolean | string>;
  // Optional filename allowlist — when present, only these files (relative
  // to the collection prefix) are stacked. Omit to stack the whole prefix.
  // Populated from the CollectionDetail filter flow so the user can cull
  // bad frames before spending EC2 time on them.
  includeFiles?: string[];
  // Optional batch id correlating N jobs launched from one user action
  // (Split axis fan-out or Panalyzer panel fan-out). Format
  // `<origin>-<slug>-<yyyymmdd>-<hhmmss>-<hex4>`, origin ∈ {split, panel}.
  // Built with buildBatchId in services/batchId.ts; server validates.
  batchId?: string;
}
export interface StartJobResp {
  jobId: string;
  runId: string;
  instanceId: string;
  status: 'launched';
  scriptId: ScriptId;
  effectiveParams: Record<string, number | boolean | string>;
  inputPrefix: string;
  outputPrefix: string;
}

export type JobState =
  | 'uploading'
  | 'uploaded'
  | 'pending'     // record inserted, credits debited, waiting for scheduler Lambda to launch EC2
  | 'running'     // EC2 instance up; sub-stage in `stage`
  | 'succeeded'
  | 'failed'
  | 'interrupted' // AWS spot reclaim — retry is the natural next step (credits already restored)
  | 'cancelled';

export interface JobSummary {
  jobId: string;
  runId: string;
  collectionId: string;
  // Optional user-supplied label. Falls back to collectionId when absent.
  name?: string;
  scriptId: ScriptId;
  instanceType: string;
  createdAt: string;          // ISO
  state: JobState;
  // Human-readable sub-stage while status=running. e.g. "Installing Siril",
  // "Stacking", "Uploading result". Set by the EC2 user-data at each
  // stage boundary. Absent for terminal states.
  stage?: string;
  stageStartedAt?: string;    // ISO — when the current stage began
  // Billable window written by the EC2 user-data: startedAt at "running",
  // endedAt at terminal state. runtimeSeconds is denormalized so cost calcs
  // don't have to parse timestamps. Absent while the job is still in
  // created/uploading — endedAt+runtimeSeconds absent until terminal.
  startedAt?: string;
  endedAt?: string;
  runtimeSeconds?: number;
  outputPrefix?: string;
  resultKey?: string;
  // Public CloudFront URL to the autostretched preview.jpg. Only set
  // when state=succeeded and the master's preview exists. Lets list
  // rows (Jobs page, Dashboard Recent stacks) render a thumbnail
  // without an N+1 GET per row.
  previewUrl?: string;
  // 512px sibling of previewUrl — prefer this in list-row thumbnails so
  // we don't pull a 1280px JPEG for a 48-64px square.
  previewThumbUrl?: string;
  error?: string;
  // Optional filename allowlist supplied when the job started ("stack N
  // filtered frames" flow). Absent = every FITS in the collection was
  // used. JobDetail reads this to compute exact frame count + integration.
  includeFiles?: string[];
  // FrameCount + drizzle are captured at job creation and drive the
  // credit debit formula — see computeCredits() in services/instances.ts.
  // Zero on pre-2026-08-19 jobs (grandfathered — no debit written).
  frameCount?: number;
  drizzle?: number;
  // Summed exptime across every FITS this job stacked (seconds). Rendered
  // as a "3h 24m"-style badge in the JobsList hero. Absent/zero on legacy
  // rows and on jobs where the analyzer hadn't populated exptime at start
  // time — client treats zero as unknown and hides the badge.
  integrationSeconds?: number;
  // Correlates N jobs launched from one user action. Prefix (`split-` /
  // `panel-`) types the origin so the list can label the batch card.
  // Absent on un-batched single launches and on pre-BatchID jobs.
  batchId?: string;
}

// ---- Public functions ----

/** GET /profile — plan, credit balance, storage quota. Under the anniversary
 *  recompute model, balance is recomputed from source (ledger + jobs) on
 *  every read, no lazy grant on this path. Pricing shape: source of truth
 *  is jobDebitCredits in lambda-start-job/ledger.go, mirrored by
 *  computeCredits() in services/instances.ts. */
export interface ProfileResp {
  plan: string;
  credits: {
    /** SUM(grants + subs + topups − refunds) − SUM(pending/running/succeeded
     *  job.DebitCredits). Can go negative if handleStartJob was bypassed. */
    balance: number;
    /** Per-month grant amount for the caller's tier. For paid tiers this
     *  is the sub grant; for first-light it's the anniv grant. */
    monthlyGrant: number;
    /** RFC3339. First-light: next anniv day (clamped to month end).
     *  Legacy paid users may still populate this from Stripe; if absent,
     *  callers should fall back to subscription.renewalAt. */
    nextGrantAt?: string;
    /** firstLightStockpileCap — present ONLY for first-light. Absent for
     *  beta / paid / admin. Frontend detects tier-with-cap by field
     *  presence (see PRD 2026-08-30-anniversary-credit-grants). */
    cap?: number;
    /** True iff on the next anniv day the cron will issue (balance < cap).
     *  Present ONLY for first-light. Frontend uses this to switch the
     *  secondary caption between "next grant …" and "at cap — pauses". */
    willIssueNext?: boolean;
    /** SUM(job.DebitCredits for pending+running jobs). Present for all
     *  tiers. Lets the UI show reserved-but-not-yet-charged credits
     *  without a second /jobs Query. */
    pendingSpend: number;
  };
  storage: {
    // Chargeable footprint: workingBytes + archivedBytes * archiveDiscount.
    // This is the value the quota bar compares against quotaBytes.
    usedBytes: number;
    // Raw component sums — the dashboard stacks them as two colored
    // segments (working: teal, archive: violet). archiveDiscount tells
    // the UI what multiplier the backend applied so tooltips can show
    // "archived: 100 GB (counts as 70 GB)".
    workingBytes: number;
    archivedBytes: number;
    archiveDiscount: number;
    quotaBytes: number;
  };
  /** Present when the caller has a subscription row (any status).
   *  Absent for admin / beta / first-light fallback tiers. */
  subscription?: {
    /** "active" | "past_due" | "cancelled". */
    status: string;
    /** ISO-8601 next-invoice timestamp. Empty until Stripe reports one. */
    renewalAt?: string;
    /** ISO-8601 timestamp set when a cancel is pending at period end. */
    cancelAt?: string;
    /** Last 4 digits of the default payment method, when surfaced. */
    paymentMethodLast4?: string;
  };
}
export const getProfile = (opts: FetchOpts) =>
  request<ProfileResp>('GET', '/profile', undefined, opts);

/** GET /profile/breakdown — raw components of the credit balance for
 *  debug/self-audit. Every ledger row and every job with a debit, so the
 *  user can eyeball the recompute-from-source math. Not called on page
 *  load; the Profile page fetches lazily when the "Balance breakdown"
 *  <details> block is first opened. */
export interface ProfileBreakdownLedgerRow {
  /** Composite DDB sort key, e.g. "grant#anniv#2026-09", "debit#job#abc-123". */
  eventKey: string;
  /** Coarse category — matches the eventKey prefix. */
  type: string;
  /** Signed integer. Positive = grant/topup, negative = refund/legacy-debit. */
  credits: number;
  /** RFC3339. Backend already sorts DESC by this. */
  timestamp: string;
  /** Human-readable note (may be empty). */
  note: string;
  /** Populated only for debit rows (empty string otherwise). */
  jobId: string;
}

export interface ProfileBreakdownJob {
  jobId: string;
  /** User-supplied label (may be empty). */
  name: string;
  state: JobState;
  /** Positive integer — the credit cost charged to this job.
   *  0 for free-tier stacks (under the free-frame floor) and for
   *  grandfathered pre-metering rows. */
  debitCredits: number;
  frameCount: number;
  drizzle: number;
  createdAt: string;
  /** Only present on `jobs.all`. Authoritative — true iff this job is
   *  currently subtracted from balance (i.e. `jobs.active` also carries it).
   *  Absent on `jobs.active` (implicitly true for that array). */
  counted?: boolean;
}

export interface ProfileBreakdown {
  userSub: string;
  plan: string;
  ledger: {
    rows: ProfileBreakdownLedgerRow[];
    /** SUM(rows.credits) — includes debit rows that recompute ignores. */
    sum: number;
    /** SUM(non-debit rows) — the number recompute actually uses. */
    counted: number;
    /** len([r for r in rows if r.type == 'debit']) — reconciler-only rows. */
    skippedDebitRows: number;
  };
  jobs: {
    /** Jobs whose debitCredits count against balance today (typically
     *  pending/running/succeeded). */
    active: ProfileBreakdownJob[];
    /** SUM(active[].debitCredits). */
    sum: number;
    /** Every job attempt the user has made — including failed, cancelled,
     *  and free-tier (debitCredits=0). Superset of `active`. Populated by
     *  the v2 backend; may be undefined during the deploy window before
     *  the backend push lands, in which case UIs fall back to `active`. */
    all?: ProfileBreakdownJob[];
  };
  /** ledger.counted - jobs.sum. Ground truth. */
  balance: number;
}

export const getProfileBreakdown = (opts: FetchOpts) =>
  request<ProfileBreakdown>('GET', '/profile/breakdown', undefined, opts);

/** GET /catalog — script definitions + instance whitelist. Unauthed. */
export const getCatalog = (opts: FetchOpts = {}) =>
  request<{ scripts: CatalogEntry[]; instances: InstanceOption[] }>(
    'GET', '/catalog', undefined, opts,
  );

/** One row in StatsResponse.showcase — a numbers-only snapshot of a
 *  recent succeeded stack for the landing-page proof strip. No user
 *  labels, targets, or thumbnails: keep it that way. */
export interface ShowcaseJob {
  frameCount: number;
  drizzle?: number;
  instanceType?: string;
  runtimeSeconds?: number;
  integrationSeconds?: number;
  scriptId?: string;
  endedAt?: string;
}

/** GET /stats — public site-wide aggregates for the marketing widget. */
export interface StatsResponse {
  updatedAt: string;
  stacksCompleted: number;
  stacksTotal: number;
  lightFrames: number;
  collections: number;
  storageBytes: number;
  integrationSeconds: number;
  computeSeconds: number;
  astronomers: number;
  /** Distinct shareIds in the anonymous drop-a-FITS flow (S3
   *  public-preview/ prefix). Rolling 30d — lifecycle rule expires
   *  the prefix. */
  anonymousShares: number;
  /** Recent succeeded stacks — landing-page "real workloads ran here"
   *  strip. Populated by lambda-stats-updater on its 5-minute tick;
   *  absent on old cached rows. */
  showcase?: ShowcaseJob[];
  /** Ops message — surfaced as the yellow site-wide banner. Empty/undefined
   *  when nothing is going on. Sourced from the statName=ops row (see
   *  lambda-start-job/stats.go and infra/code/dynamodb_stats.tf). */
  banner?: string;
}
export const getStats = (opts: FetchOpts = {}) =>
  request<StatsResponse>('GET', '/stats', undefined, opts);

/** POST /upload/init — begin multipart upload. */
export const initUpload = (body: UploadInitReq, opts: FetchOpts) =>
  request<UploadInitResp>('POST', '/upload/init', body, opts);

/** POST /upload/part — presign a single UploadPart URL. */
export const signPart = (body: UploadPartReq, opts: FetchOpts) =>
  request<UploadPartResp>('POST', '/upload/part', body, opts);

/** POST /upload/complete — finalize the object. */
export const completeUpload = (body: UploadCompleteReq, opts: FetchOpts) =>
  request<UploadCompleteResp>('POST', '/upload/complete', body, opts);

/** POST /jobs — launch a stacking job on the given collection. */
export const startJob = (body: StartJobReq, opts: FetchOpts) =>
  request<StartJobResp>('POST', '/jobs', body, opts);

/** GET /jobs — list caller's jobs, newest first.
 *
 *  Optional server-side filters (backend ignores unknown params so calls
 *  from an older backend transparently fall back to full-list + client
 *  filter — see docs/prds/2026-08-29-webapp-performance.md phase 4):
 *  - `state`: enum `active|created|uploading|uploaded|running|succeeded|failed|cancelled`
 *    where `active` unions the four non-terminal states
 *  - `collectionId`: exact match on the collection prefix
 *  - `limit`: cap on returned rows, 1..500
 */
export interface ListJobsOpts extends FetchOpts {
  state?: 'active' | 'pending' | 'uploading' | 'uploaded' | 'running' | 'succeeded' | 'failed' | 'interrupted' | 'cancelled';
  collectionId?: string;
  limit?: number;
}

export const listJobs = (opts: ListJobsOpts) => {
  const qs = new URLSearchParams();
  if (opts.state) qs.set('state', opts.state);
  if (opts.collectionId) qs.set('collectionId', opts.collectionId);
  if (opts.limit) qs.set('limit', String(opts.limit));
  const path = qs.toString() ? `/jobs?${qs}` : '/jobs';
  return request<{ jobs: JobSummary[] }>('GET', path, undefined, opts);
};

/** GET /jobs/{id} — job detail. downloadUrl + resultFilename are only
 *  present when the job is state=succeeded and a result file was produced. */
export interface JobDetail extends JobSummary {
  effectiveParams: Record<string, number | boolean | string>;
  downloadUrl?: string;
  resultFilename?: string;
  // Signed URL to an autostretched preview JPEG rendered by Siril
  // alongside the master. ~1-2 MB, safe to display in an <img>. Absent
  // means the preview render failed or hasn't been produced (older jobs).
  previewUrl?: string;
  // Public CloudFront URLs of per-channel mono FITS split from the master
  // after stacking (Siril `split R G B`). Keys are channel labels
  // ("R"/"G"/"B"; future presets may add "Ha"/"OIII"). Absent on mono
  // masters or when the split failed. For client-side recombination
  // (HOO, faux Hubble, LRGB) without re-running the stack.
  channelUrls?: Record<string, string>;
}
export const getJob = (id: string, opts: FetchOpts) =>
  request<JobDetail>('GET', `/jobs/${encodeURIComponent(id)}`, undefined, opts);

/** POST /jobs/{id}/cancel — terminate the EC2 instance (if any) and mark the
 *  job cancelled. Only works while state ∈ {created, running, uploading,
 *  uploaded}; returns 400 NOT_CANCELLABLE for terminal states. */
export const cancelJob = (id: string, opts: FetchOpts) =>
  request<JobDetail>('POST', `/jobs/${encodeURIComponent(id)}/cancel`, {}, opts);

/** CloudWatch time-series for the job's spot instance. Feeds the "Advanced"
 *  panel on JobDetail so we can see whether a slow stack was CPU / RAM /
 *  disk / network bound. Empty series arrays are normal for a job in its
 *  first ~60s (CWA publishes at 1-min intervals). */
export interface JobMetricPoint {
  t: string;
  v: number;
}
export interface JobMetrics {
  instanceId: string;
  startTime: string;
  endTime: string;
  periodSeconds: number;
  series: Record<string, JobMetricPoint[]>;
  note?: string;
}
export const getJobMetrics = (id: string, opts: FetchOpts) =>
  request<JobMetrics>('GET', `/jobs/${encodeURIComponent(id)}/metrics`, undefined, opts);

/** POST /jobs/{id}/retry — re-launch a terminal job with the same
 *  collectionId, script, params, instance, and filename. Backend delegates
 *  to the same handler as POST /jobs; returns the NEW job's launch response
 *  (fresh jobId). Only allowed on failed/cancelled/succeeded jobs. */
export const retryJob = (id: string, opts: FetchOpts) =>
  request<StartJobResp>('POST', `/jobs/${encodeURIComponent(id)}/retry`, {}, opts);

/** DELETE /jobs/{id} — soft-delete a terminal stack: wipes S3 artifacts
 *  (master, preview, channel splits, logs) and marks the DDB row deleted
 *  so it disappears from list + detail. Only allowed on terminal states
 *  (succeeded/failed/cancelled/interrupted); active jobs must be
 *  cancelled first. Idempotent — a repeat call returns 404. */
export const deleteJob = (id: string, opts: FetchOpts) =>
  request<{ jobId: string; deleted: boolean }>(
    'DELETE', `/jobs/${encodeURIComponent(id)}`, undefined, opts,
  );

/** POST /jobs/{id}/label — set the user-facing name on a stack. Trimmed,
 *  non-empty, capped at 60 chars server-side. Returns the accepted name. */
export const labelJob = (id: string, name: string, opts: FetchOpts) =>
  request<{ jobId: string; name: string }>(
    'POST', `/jobs/${encodeURIComponent(id)}/label`, { name }, opts,
  );

// ---- Public share links ----
//
// One active share per succeeded stack. Slug is 96 bits of entropy; the
// public /s/{slug} endpoint is unauthenticated, so the slug IS the auth.
// Owner-authed create/get/delete round out the CRUD from the JobDetail
// "Share" affordance.

export interface JobShare {
  slug: string;
  url: string;         // absolute, e.g. https://pancakestack.net/s/<slug>
  createdAt: string;   // ISO
}

/** POST /jobs/{id}/share — create (or fetch existing) public share link. */
export const createJobShare = (id: string, opts: FetchOpts) =>
  request<JobShare>('POST', `/jobs/${encodeURIComponent(id)}/share`, {}, opts);

/** GET /jobs/{id}/share — 404 if the stack isn't shared. */
export const getJobShare = (id: string, opts: FetchOpts) =>
  request<JobShare>('GET', `/jobs/${encodeURIComponent(id)}/share`, undefined, opts);

/** DELETE /jobs/{id}/share — revoke; idempotent. */
export const deleteJobShare = (id: string, opts: FetchOpts) =>
  request<{ revoked: boolean; slug?: string }>(
    'DELETE', `/jobs/${encodeURIComponent(id)}/share`, undefined, opts,
  );

/** Public share payload — /s/{slug}. No auth. Preview URL is CF-unsigned;
 *  download URL is a short-TTL S3 presign that expires ~1h after fetch. */
export interface PublicShare {
  slug: string;
  collectionId: string;
  stackName: string;
  createdAt: string;
  previewUrl?: string;
  downloadUrl?: string;
  downloadExpires?: string;
  resultFilename?: string;
  masterSizeBytes?: number;
  frameCount: number;
  integrationSeconds: number;
  // Stack-time + hardware — populated for succeeded jobs so the public
  // share page can showcase "N,NNN frames stacked in Xm Ys on 32c · 128 GB".
  runtimeSeconds?: number;
  instanceType?: string;
  exptime?: number;
  filter?: string;
  instrume?: string;
  telescop?: string;
  dateObs?: string;
  object?: string;
  // Geek panel — sampled first-non-empty from the collection's FITS
  // metadata. All optional so the SPA skips rows the sensor didn't
  // populate.
  gain?: number;
  offset?: number;
  ccdTemp?: number;   // °C
  setTemp?: number;   // °C
  focalLen?: number;  // mm
  aperture?: number;  // mm
  xPixSz?: number;    // µm
  yPixSz?: number;
  xBinning?: number;
  yBinning?: number;
  width?: number;     // px
  height?: number;
  bitpix?: number;
  ra?: number;        // degrees
  dec?: number;
  airmass?: number;
  moonPhas?: number;  // 0..1 illumination
  poweredBy: string;
}

/** GET /s/{slug} — unauthenticated. Fetches the public share view. */
export const getPublicShare = (slug: string) =>
  request<PublicShare>('GET', `/s/${encodeURIComponent(slug)}`, undefined, {});

/** POST /public/preview/init — anonymous marketing-widget presign. Zero
 *  auth, IP-rate-limited on the backend. Returns a shareId + the
 *  presigned PUT URL for the FITS + the expected preview/thumb URLs the
 *  browser will poll for once the fits-thumbnail Lambda has rendered.
 *  See lambda-start-job/public_preview.go for validation rules and
 *  the 30-day expiry (S3 lifecycle rule on the public-preview prefix). */
export interface PublicPreviewInitReq {
  filename: string;
  sizeBytes: number;
  contentType?: string;
}
export interface PublicPreviewInitResp {
  shareId: string;
  uploadUrl: string;
  key: string;
  previewUrl: string;
  thumbUrl: string;
  expiresIn: number;
  maxBytes: number;
}
export const publicPreviewInit = (body: PublicPreviewInitReq) =>
  request<PublicPreviewInitResp>('POST', '/public/preview/init', body, {});

/** GET /public/preview/{shareId} — unauthenticated share-page metadata.
 *  Lists the S3 prefix on the backend (no DDB); returns everything the
 *  share page needs to render cross-device. The three URLs are
 *  CloudFront (files.pancakestack.net) and unsigned — the raw .fit is
 *  served under a dedicated public-preview/*.fit behavior. */
export interface PublicPreview {
  shareId: string;
  filename: string;
  sizeBytes: number;
  previewUrl: string;
  thumbUrl: string;
  downloadUrl: string;
  createdAt: string;
}
export const getPublicPreview = (shareId: string) =>
  request<PublicPreview>('GET', `/public/preview/${encodeURIComponent(shareId)}`, undefined, {});

export interface CollectionEntry {
  collectionId: string;
  // displayName is what the UI should render everywhere the user sees a
  // collection. collectionId is the immutable S3 prefix / DDB PK segment
  // and is only surfaced in URLs and diagnostics.
  displayName: string;
  archiveFilename?: string;      // e.g. "lights.zip"
  fitsCount?: number;            // individual .fit/.fits under the prefix
  archiveCount?: number;         // .tar/.tar.gz/.tar.zst/.zip
  sizeBytes?: number;
  lastModified?: string;         // ISO
  createdAt?: string;            // ISO — when the DDB row was materialized
  // Archive state — archived collections are read-only from the web,
  // block job starts entirely, and expose an unarchive countdown after
  // a 30-day min duration on Standard-IA. archivedAt is unix seconds.
  archived?: boolean;
  archivedAt?: number;
  // Fine-grained transition state, distinct from `archived`. Written
  // by the async storage-transition Lambda. Values: "" | "archiving" |
  // "archived" | "unarchiving" | "archive_failed" | "unarchive_failed".
  // SPA reads this to render progress pills and disable buttons during
  // the transitional states.
  archiveState?: string;
  archiveError?: string;
  // Set while an interactive uploader (currently `pancakestack seestar
  // sync`) has heartbeated within the last ~15s. Backend derives this
  // from the collection row at read time; no extra fetch. Cleared
  // automatically on the next list when the client stops beating.
  liveImport?: {
    state: 'importing';
    folder?: string;
    filesSeen?: number;
    since?: string;
  };
  // True when the backend detected an archive-in-flight or a live
  // drive-import EC2 spot at read time. Drives the "Importing…" badge
  // on the CollectionCard.
  pendingImport?: boolean;
  // Populated when at least one S3 multipart upload is in flight on
  // the collection prefix — CLI (`pancakestack upload`), browser drop-
  // zone, or any other multipart source. Backend aggregates
  // ListMultipartUploads across the user prefix. Alive=true means at
  // least one multipart has landed a part within the last minute;
  // alive=false = "probably abandoned" — drives the amber warning
  // badge on the CollectionCard.
  pendingUpload?: {
    alive: boolean;
    bytesUploaded?: number;
    since?: string; // RFC3339, oldest Initiated across the prefix
  };
}

export interface ArchiveCollectionResp {
  collectionId: string;
  archived: boolean;
  archivedAt: number;
  // Transitional state written when the API returned 202. The archive-op
  // worker Lambda tars the FITS and uploads to Backblaze B2 async; the
  // SPA polls the collection row to observe archiveState flipping to
  // "archived" (or "archive_failed") when the worker finishes.
  archiveState: string;
}

/** POST /collections/{id}/archive — park the collection: mark archived,
 *  kick the async storage-transition Lambda. Returns 202 with
 *  archiveState="archiving"; SPA polls for terminal state. Requires the
 *  caller to echo the collectionId in the body — type-to-confirm guard. */
export const archiveCollection = (
  id: string,
  confirm: string,
  opts: FetchOpts,
) =>
  request<ArchiveCollectionResp>(
    'POST',
    `/collections/${encodeURIComponent(id)}/archive`,
    { confirm },
    opts,
  );

/** POST /collections/{id}/unarchive — 30 days after archiveAt, restore
 *  the collection to Standard storage. Returns 202 with
 *  archiveState="unarchiving"; async transition Lambda flips
 *  archived=false and drops archiveState when done. Backend enforces
 *  the 30-day gate + a free-quota headroom check before enqueueing. */
export const unarchiveCollection = (id: string, opts: FetchOpts) =>
  request<{ collectionId: string; archived: boolean; archiveState: string }>(
    'POST',
    `/collections/${encodeURIComponent(id)}/unarchive`,
    {},
    opts,
  );

export interface ImportDriveResp {
  collectionId: string;
  kind: 'file' | 'folder';
  filename: string;
  bytes: number;
  instanceId: string;
  // File-import fields (absent for folder imports)
  fileId?: string;
  archiveKey?: string;
  // Folder-import fields (absent for file imports)
  folderId?: string;
  fileCount?: number;
}

/** POST /collections/{id}/import/drive — kick off a Google-Drive import.
 *  Accepts either shape:
 *  - Archive file (`/file/d/{id}/…`, raw ID): worker downloads the archive
 *    to S3; the normalizer extracts to individual FITS.
 *  - Folder (`/drive/folders/{id}`): server lists direct FITS children,
 *    worker parallel-downloads and s3-syncs each into the collection
 *    prefix. No normalizer step.
 *
 *  Both must be shared as "Anyone with the link".
 *
 *  Rejects with DRIVE_NOT_PUBLIC (403), DRIVE_NOT_FOUND (404),
 *  DRIVE_UNSUPPORTED_TYPE (400, file case: non-archive),
 *  DRIVE_FOLDER_EMPTY (400, folder case: no FITS at top level),
 *  DRIVE_FOLDER_TOO_LARGE (413, folder case),
 *  STILL_NORMALIZING (409), or STORAGE_QUOTA_EXCEEDED (402). */
export const importFromDrive = (
  collectionId: string,
  driveUrl: string,
  opts: FetchOpts,
) =>
  request<ImportDriveResp>(
    'POST',
    `/collections/${encodeURIComponent(collectionId)}/import/drive`,
    { driveUrl },
    opts,
  );

/** GET /collections — list caller's uploaded collections. */
export const listCollections = (opts: FetchOpts) =>
  request<{ collections: CollectionEntry[] }>('GET', '/collections', undefined, opts);

/** DELETE /collections/{id} — remove every S3 object under the collection.
 *  Does NOT delete job history; jobs referencing this collection still exist
 *  but can no longer be retried. */
export const deleteCollection = (id: string, opts: FetchOpts) =>
  request<{ collectionId: string; deleted: number }>(
    'DELETE', `/collections/${encodeURIComponent(id)}`, undefined, opts,
  );

/** POST /collections — materialize an empty collection row without
 *  triggering an upload or drive-import. Idempotent: repeated calls
 *  return `created: false` with the existing row. Response body is
 *  always the row we know is on the table, so the caller can navigate
 *  straight to /collections/{id} without a follow-up GET. */
export const createCollection = (
  collectionId: string,
  opts: FetchOpts,
) =>
  request<{
    collectionId: string;
    displayName: string;
    createdAt: string;
    created: boolean;
  }>(
    'POST',
    '/collections',
    { collectionId },
    opts,
  );

/** POST /collections/{id}/label — set a new displayName on the collection.
 *  The collectionId (S3 prefix, files-table PK) is immutable; only the
 *  human-readable label changes. One DDB UpdateItem — no S3 moves, no
 *  files-table PK rewrites. */
export const labelCollection = (
  id: string,
  displayName: string,
  opts: FetchOpts,
) =>
  request<{ collectionId: string; displayName: string }>(
    'POST',
    `/collections/${encodeURIComponent(id)}/label`,
    { displayName },
    opts,
  );

/** DELETE /collections/{id}/files/{filename} — remove one file plus its
 *  sibling thumb/preview JPEGs and its DDB metadata row. Idempotent. */
export const deleteCollectionFile = (
  id: string,
  filename: string,
  opts: FetchOpts,
) =>
  request<{ collectionId: string; filename: string; deleted: number }>(
    'DELETE',
    `/collections/${encodeURIComponent(id)}/files/${encodeURIComponent(filename)}`,
    undefined,
    opts,
  );

/** Max filenames per batchDeleteCollectionFiles call — mirrors the
 *  server's batchDeleteMaxN. Larger deletes must be chunked. */
export const BATCH_DELETE_MAX = 1000;

/** POST /collections/{id}/files/batch-delete — bulk-wipe a list of files
 *  in one round-trip. Server-side batches S3 DeleteObjects + DDB
 *  BatchWriteItem + one atomic counter decrement, so a 1000-file wipe
 *  takes ~5s wall time instead of 1000 sequential HTTP calls at
 *  concurrency 6. Cap: BATCH_DELETE_MAX per request — chunk larger. */
export const batchDeleteCollectionFiles = (
  id: string,
  filenames: string[],
  opts: FetchOpts,
) =>
  request<{ collectionId: string; deleted: number; failed?: string[] }>(
    'POST',
    `/collections/${encodeURIComponent(id)}/files/batch-delete`,
    { filenames },
    opts,
  );

// ---- Bulk upload (drag-drop of N individual FITS files) ----
//
// Backend: POST /upload/presign takes up to 500 filenames, returns N
// presigned single-PUT URLs. Each FITS is ~30 MB, well under S3's 5 GiB
// single-PUT cap, so no multipart machinery needed per file.

export interface PresignBulkFile {
  name: string;
  contentType?: string;
  // SHA-256 of the file bytes, lowercase hex. Required — backend
  // rejects known duplicates before returning a URL.
  sha256: string;
  // Per-file size in bytes. Required — backend sums non-duplicate
  // sizes across the batch and rejects the whole request if it would
  // push the caller over their plan cap.
  sizeBytes: number;
}

// StorageQuotaError is the structured 402 payload the upload endpoints
// (and drive-import) return when the request would push the caller
// past their plan storage cap. The `error` field is a human-readable
// summary; the numeric fields let callers render "X GB over Y GB cap"
// without parsing the message.
export interface StorageQuotaError {
  code: 'STORAGE_QUOTA_EXCEEDED';
  error: string;
  used: number;
  quota: number;
  requested: number;
  shortfall: number;
}

// isStorageQuotaError narrows an unknown error (typically an ApiError or
// its .payload) to the typed StorageQuotaError shape. Callers do
// `if (isStorageQuotaError(e.payload)) { … render inline UI … }` to
// pull the numeric shortfall out without re-parsing the message.
export const isStorageQuotaError = (e: unknown): e is StorageQuotaError =>
  typeof e === 'object' && e !== null
  && (e as { code?: string }).code === 'STORAGE_QUOTA_EXCEEDED'
  && typeof (e as { shortfall?: unknown }).shortfall === 'number';

export interface PresignedFile {
  name: string;
  key?: string;
  url?: string;
  expiresIn?: number;
  // Backend returns skipped=true when this checksum already exists
  // in the collection. Client must NOT PUT bytes for this entry.
  skipped?: boolean;
  reason?: string;
  duplicateOf?: string;
}

/** POST /upload/presign — batch presign up to 500 filenames. */
export const presignBulk = (
  collectionId: string,
  files: PresignBulkFile[],
  opts: FetchOpts,
) =>
  request<{ files: PresignedFile[] }>(
    'POST',
    '/upload/presign',
    { collectionId, files },
    opts,
  );

// contentTypeFor mirrors the CLI's implementation. Matters because the
// backend bakes Content-Type into the presigned URL — a mismatch on
// PUT returns 403 SignatureDoesNotMatch from S3.
export const contentTypeFor = (name: string): string => {
  const l = name.toLowerCase();
  if (l.endsWith('.fit') || l.endsWith('.fits')) return 'application/fits';
  if (l.endsWith('.fz')) return 'application/octet-stream';
  if (l.endsWith('.zip')) return 'application/zip';
  if (l.endsWith('.tar')) return 'application/x-tar';
  return 'application/octet-stream';
};

export interface BulkUploadResult {
  name: string;
  key?: string;
  error?: string;
  // Backend deduped this file — no PUT was attempted. Not an error.
  skipped?: boolean;
  reason?: string;
  duplicateOf?: string;
}

export interface BulkUploadOptions {
  collectionId: string;
  files: File[];
  concurrency?: number;
  token: string;
  // Called each time a file finishes (success, fail, or skipped).
  // `done`/`total` are file counts; `bytesUploaded` tracks transferred
  // bytes only (skipped files contribute nothing).
  onProgress?: (state: {
    done: number;
    total: number;
    bytesUploaded: number;
    totalBytes: number;
    failed: number;
    skipped: number;
  }) => void;
  // Fires whenever the hasher makes progress. Client-side SHA-256 for
  // 3000 files takes 15-30s; UI needs to show life.
  onHashProgress?: (state: { hashed: number; total: number }) => void;
}

// Max files per presign batch — matches backend cap.
const PRESIGN_BATCH_SIZE = 500;

/** uploadFilesBulk — bulk-presign + concurrent PUTs. Returns per-file
 *  results so caller can decide how to surface partial failures.
 *
 *  Scales to 3000+ files: presigns in batches of 500, uploads with
 *  concurrency (default 6). No retry on individual failures for MVP —
 *  caller can re-drop and skip-if-exists would pick them up (future). */
export const uploadFilesBulk = async (
  opts: BulkUploadOptions,
): Promise<BulkUploadResult[]> => {
  const concurrency = opts.concurrency ?? 6;
  const totalBytes = opts.files.reduce((sum, f) => sum + f.size, 0);

  // Hash every FITS client-side first. Backend uses the sha256 to
  // reject duplicates before returning a presigned URL, so the client
  // never spends bandwidth PUTting bytes the collection already has.
  //
  // Archives skip the hash — each archive upload is unique-per-user
  // (they're transport, not content), and hashing a multi-GB tar
  // wastes time; on a 50+ GB tar file.arrayBuffer() blows the browser's
  // ArrayBuffer cap and throws NotReadableError before init even fires.
  // Backend allows empty sha256 for archive extensions (see
  // multipart.go presign handler).
  const filesToHash = opts.files.filter((f) => !isArchiveFilename(f.name));
  const hashByName = new Map<string, string>();
  const hashResults = await hashFilesInParallel(filesToHash, 4, opts.onHashProgress);
  filesToHash.forEach((f, i) => hashByName.set(f.name, hashResults[i]));
  const shas = opts.files.map((f) => hashByName.get(f.name) ?? '');

  // Presign everything up-front in batches of 500. Simpler than pipelining
  // presign with upload, finishes in a few seconds even for thousands.
  const presigned: PresignedFile[] = [];
  for (let i = 0; i < opts.files.length; i += PRESIGN_BATCH_SIZE) {
    const batch = opts.files.slice(i, i + PRESIGN_BATCH_SIZE);
    const shaSlice = shas.slice(i, i + PRESIGN_BATCH_SIZE);
    const resp = await presignBulk(
      opts.collectionId,
      batch.map((f, j) => ({
        name: f.name,
        contentType: contentTypeFor(f.name),
        sha256: shaSlice[j],
        sizeBytes: f.size,
      })),
      { token: opts.token },
    );
    presigned.push(...resp.files);
  }

  // File name → File object lookup — order-independent since presign
  // response order matches request order but this is safer.
  const byName = new Map(opts.files.map((f) => [f.name, f]));

  const results: BulkUploadResult[] = new Array(presigned.length);
  let done = 0;
  let bytesUploaded = 0;
  let failed = 0;
  let skipped = 0;

  // Simple worker pool: N goroutines pop from a shared queue.
  let cursor = 0;
  const worker = async () => {
    while (true) {
      const i = cursor++;
      if (i >= presigned.length) return;
      const pf = presigned[i];

      // Server said "already have it" — record and move on. Skipped
      // files count toward `done` AND their bytes count toward
      // `bytesUploaded`: totalBytes was computed across every queued
      // file, so if we didn't credit skipped bytes the progress bar
      // (which uses the byte ratio) would lag behind the file counter
      // and hug the left even at 90% done.
      if (pf.skipped) {
        results[i] = {
          name: pf.name,
          skipped: true,
          reason: pf.reason,
          duplicateOf: pf.duplicateOf,
        };
        skipped++;
        done++;
        bytesUploaded += byName.get(pf.name)?.size ?? 0;
        opts.onProgress?.({ done, total: presigned.length, bytesUploaded, totalBytes, failed, skipped });
        continue;
      }

      const file = byName.get(pf.name)!;
      try {
        await putPresigned(pf.url!, file, contentTypeFor(pf.name));
        results[i] = { name: pf.name, key: pf.key };
      } catch (e) {
        results[i] = { name: pf.name, key: pf.key, error: (e as Error).message };
        failed++;
      }
      done++;
      bytesUploaded += file.size;
      opts.onProgress?.({ done, total: presigned.length, bytesUploaded, totalBytes, failed, skipped });
    }
  };
  const workers = Array.from({ length: Math.min(concurrency, presigned.length) }, worker);
  await Promise.all(workers);
  return results;
};

// hashFilesInParallel computes SHA-256 for each File. crypto.subtle
// takes an ArrayBuffer, so we read the file into memory once (typical
// FITS is 20-40 MB — fine) and hash. Bounded concurrency so 2500
// simultaneous reads don't OOM the browser.
export const hashFilesInParallel = async (
  files: File[],
  concurrency: number,
  onProgress?: (state: { hashed: number; total: number }) => void,
): Promise<string[]> => {
  const results = new Array<string>(files.length);
  let cursor = 0;
  let hashed = 0;
  const total = files.length;
  const worker = async () => {
    while (true) {
      const i = cursor++;
      if (i >= files.length) return;
      results[i] = await hashFile(files[i]);
      hashed++;
      onProgress?.({ hashed, total });
    }
  };
  const workers = Array.from({ length: Math.min(concurrency, files.length) }, worker);
  await Promise.all(workers);
  return results;
};

// hashFile returns the lowercase-hex SHA-256 of a File's bytes.
export const hashFile = async (file: File | Blob): Promise<string> => {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  const bytes = new Uint8Array(digest);
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, '0');
  }
  return out;
};

const putPresigned = async (
  url: string,
  blob: Blob,
  contentType: string,
): Promise<void> => {
  const resp = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: blob,
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`S3 ${resp.status}: ${text.slice(0, 200)}`);
  }
};

export interface CollectionFile {
  name: string;
  sizeBytes: number;
  lastModified: string;
  kind: 'fits' | 'archive' | 'other';
  // Signed CloudFront URLs to sibling JPEGs generated by the fits-thumbnail
  // Lambda. Absent while the SQS queue is still draining, or if the render
  // failed. Client should render a placeholder for missing URLs.
  thumbUrl?: string;
  previewUrl?: string;
  // FITS header + pixel stats — present when the DDB metadata row exists
  // (populated by the fits-thumbnail Lambda). Absent under the S3
  // fallback listing (older collections that predate the Lambda). All
  // fields the Lambda can extract are declared here; Seestar rows fill
  // in the core subset, non-Seestar setups populate more.
  object?: string;
  dateObs?: string;
  exptime?: number;
  gain?: number;
  offset?: number;
  ccdTemp?: number;
  setTemp?: number;
  filter?: string;
  imagetyp?: string;
  instrume?: string;
  telescop?: string;
  swCreate?: string;
  bayerPat?: string;
  focalLen?: number;
  aperture?: number;
  xPixSz?: number;
  yPixSz?: number;
  xBinning?: number;
  yBinning?: number;
  ra?: number;
  dec?: number;
  airmass?: number;
  moonPhas?: number;
  siteLat?: number;
  siteLong?: number;
  width?: number;
  height?: number;
  bitpix?: number;
  mean?: number;
  median?: number;
  stddev?: number;
  min?: number;
  max?: number;
  // Quality metrics from the Python analyzer Lambda (sep + astropy).
  // Absent until the analyzer runs — arrives asynchronously via SQS
  // seconds after the Go Lambda's fields.
  starCount?: number;
  fwhmMedian?: number;
  eccentricityMedian?: number;
  snrMedian?: number;
  background?: number;
  backgroundRms?: number;
  saturatedPct?: number;
  // Plate-solve output from the analyzer's solve-field call. wcsSolvedAt
  // is the sentinel — if present, the rest are the FITS WCS shape (CD
  // matrix + CRVAL/CRPIX + type codes) enough to draw the plate footprint
  // on a sky-survey overlay (Aladin) without a full WCSlib port to JS.
  wcsCrval1?: number;
  wcsCrval2?: number;
  wcsCrpix1?: number;
  wcsCrpix2?: number;
  wcsCd11?: number;
  wcsCd12?: number;
  wcsCd21?: number;
  wcsCd22?: number;
  wcsCtype1?: string;
  wcsCtype2?: string;
  wcsRadesys?: string;
  wcsPixScale?: number;
  wcsOrientation?: number;
  wcsSolvedAt?: string;
}

export interface ListCollectionFilesResp {
  files: CollectionFile[];
  nextToken?: string;
  // 'ddb' when the response came from the metadata table (rich fields
  // available); 's3' when we fell back to bucket listing (name/size only).
  source: 'ddb' | 's3';
  // URL prefix under which every file's sibling thumb+preview JPEGs live —
  // `${CDN}/input/${sub}/${collectionId}/`. `listCollectionFiles` uses this
  // to populate each file's thumbUrl+previewUrl client-side (see the
  // synthesis pass below), so callers see the same CollectionFile shape as
  // before. Backend emits this once per response instead of the two URLs
  // per row; on a 12k-frame collection that's ~2.9 MB of wire saved.
  thumbBase?: string;
  // Present when a landed tar is being unpacked by the normalize Lambda's
  // spot instance. Absent otherwise. Archives themselves are filtered out
  // of `files` — the banner replaces the misleading row.
  //
  // state="failed" means the normalize spot died and the archive is
  // stuck; recovery is a client-initiated DELETE /collections/{id}/normalize
  // then re-upload. See NormalizeProgressBanner failed variant.
  //
  // phase/done/total/updatedAt come from the <prefix>.pancakestack-progress
  // marker written by normalize.sh.tmpl at ~2s cadence. Absent when the
  // spot script hasn't started writing yet — banner falls back to
  // pre-progress copy.
  archive?: {
    state: 'extracting' | 'failed';
    bytes?: number;
    since?: string;
    phase?: 'extracting' | 'uploading';
    done?: number;
    total?: number;
    updatedAt?: string;
  };
  // Present while a Drive-import spot is downloading from Google Drive.
  // Covers both shapes: single archive (kind=file), where the normalizer
  // extracts on landing, and folder (kind=folder), where the worker
  // s3-syncs FITS directly. Derived by the backend from live
  // DescribeInstances on the drive-import spot's tags.
  //
  // Same progress fields as `archive` — phase/done/total/updatedAt come
  // from drive-folder-import.sh.tmpl's progress marker.
  driveImport?: {
    state: 'downloading';
    kind?: 'file' | 'folder';
    bytes?: number;
    fileCount?: number;
    filename?: string;
    since?: string;
    instanceId?: string;
    phase?: 'downloading' | 'uploading';
    done?: number;
    total?: number;
    updatedAt?: string;
  };
  // In-flight S3 multipart uploads on the collection prefix. Each entry
  // is an initiated-but-not-completed upload — CLI streaming a tar, or
  // an abandoned session that hasn't hit the S3 3-day auto-abort yet.
  // Frontend renders one card per entry with a Cancel button.
  pendingUploads?: PendingUpload[];
  // Present while an interactive client (currently `pancakestack seestar
  // sync`) has heartbeated within the last ~15s. Cleared automatically on
  // the next backend read once the client stops beating (Ctrl-C, network
  // drop, process death). Frontend renders a "Live import in progress"
  // banner in the same visual language as `archive` / `driveImport`.
  liveImport?: {
    state: 'importing';
    folder?: string;
    filesSeen?: number;
    since?: string;
  };
}

export interface PendingUpload {
  uploadId: string;
  filename: string;
  initiatedAt: string;      // RFC3339
  bytesUploaded: number;
  partCount: number;
  lastProgressAt?: string;  // RFC3339, empty when zero parts uploaded
  alive: boolean;           // last part landed within the alive threshold (currently 60s)
}

/** DELETE /collections/{id}/uploads/{uploadId} — abort an in-flight
 *  multipart upload. Idempotent — a UploadId that no longer exists
 *  returns {aborted: false, reason: 'not-found'}. Called from the
 *  Cancel button on each pendingUpload card. */
export const cancelPendingUpload = (
  collectionId: string,
  uploadId: string,
  opts: FetchOpts,
) =>
  request<{ aborted: boolean; key?: string; reason?: string }>(
    'DELETE',
    `/collections/${encodeURIComponent(collectionId)}/uploads/${encodeURIComponent(uploadId)}`,
    undefined,
    opts,
  );

/** DELETE /collections/{id}/normalize — recovery path for a failed
 *  extraction. Deletes the pending archive object + the progress
 *  marker so guardStillNormalizing unblocks and the user can re-upload.
 *  Returned by the "Delete archive" button on NormalizeProgressBanner's
 *  failed variant. */
export const deleteNormalize = (
  collectionId: string,
  opts: FetchOpts,
) =>
  request<{ deleted: string[] }>(
    'DELETE',
    `/collections/${encodeURIComponent(collectionId)}/normalize`,
    undefined,
    opts,
  );

/** GET /collections/{id}/files — paginated files with FITS metadata.
 *  Backend returns only a `thumbBase` prefix per response, not per-file
 *  thumb/preview URLs. This wrapper synthesizes `file.thumbUrl` and
 *  `file.previewUrl` from that prefix so callers see the same
 *  CollectionFile shape as before. The lightbox path
 *  (getCollectionFile) fetches its own signed URLs — those are needed
 *  when a preview URL migrates to a signed variant. */
// Panalyzer-view fetch — hits the dedicated by-collection-panalyzer
// GSI, which projects the full WCS matrix + width/height that SkyView's
// footprintCorners needs to draw plate footprints. Kept separate from
// listCollectionFiles because the slim-v2 GSI backing that call caps
// at 20 attributes and the WCS matrix wouldn't fit alongside the list-
// view fields (see dynamodb_files.tf's Panalyzer GSI comment).
//
// Pagination: same shape as listCollectionFiles (nextToken cursor,
// server pageSize cap). Callers typically loop until nextToken is
// empty so SkyView can render as data streams in.
export const listCollectionFilesPanalyzer = async (
  id: string,
  opts: FetchOpts & { nextToken?: string; pageSize?: number },
): Promise<ListCollectionFilesResp> => {
  const qs = new URLSearchParams();
  if (opts.nextToken) qs.set('nextToken', opts.nextToken);
  if (opts.pageSize) qs.set('pageSize', String(opts.pageSize));
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<ListCollectionFilesResp>(
    'GET', `/collections/${encodeURIComponent(id)}/panalyzer${suffix}`, undefined, opts,
  );
};

export const listCollectionFiles = async (
  id: string,
  opts: FetchOpts & { nextToken?: string; pageSize?: number },
): Promise<ListCollectionFilesResp> => {
  const qs = new URLSearchParams();
  if (opts.nextToken) qs.set('nextToken', opts.nextToken);
  if (opts.pageSize) qs.set('pageSize', String(opts.pageSize));
  const suffix = qs.toString() ? `?${qs}` : '';
  const resp = await request<ListCollectionFilesResp>(
    'GET', `/collections/${encodeURIComponent(id)}/files${suffix}`, undefined, opts,
  );
  const base = resp.thumbBase;
  if (base) {
    for (const f of resp.files) {
      if (f.kind !== 'fits') continue;
      const stem = stripFitsExt(f.name);
      // Escape spaces + specials per URI-component; matches the
      // backend's per-segment PathEscape used prior to this synthesis
      // move (see signCollectionFileURLs in collections.go).
      const enc = encodeURIComponent(stem);
      f.thumbUrl = `${base}${enc}.thumb.jpg`;
      f.previewUrl = `${base}${enc}.preview.jpg`;
    }
  }
  return resp;
};

// Mirrors stripFitsExtGo in lambda-start-job/collections.go — trims the
// FITS extension (`.fits`, `.fit`, `.fz`, case-insensitive) so the
// sibling JPEG name can be built. Anything else is returned unchanged.
const stripFitsExt = (name: string): string => {
  const lower = name.toLowerCase();
  for (const ext of ['.fits', '.fit', '.fz']) {
    if (lower.endsWith(ext)) return name.slice(0, -ext.length);
  }
  return name;
};

export interface PresignDownloadFile {
  name: string;
  key: string;
  url: string;
  expiresIn: number;
}

/** POST /collections/{id}/download/presign — batch presigned GET URLs
 *  for raw FITS. Server signs each key with the CloudFront/S3 signing
 *  key; client just navigates the browser to the URL to download. */
export const presignDownload = (
  id: string,
  names: string[],
  opts: FetchOpts,
) =>
  request<{ files: PresignDownloadFile[] }>(
    'POST',
    `/collections/${encodeURIComponent(id)}/download/presign`,
    { names },
    opts,
  );

/** Presign a single FITS by name and trigger a browser download.
 *  Uses a hidden <a download> so we stay on the current page and the
 *  browser saves with the basename (subdir-prefixed keys land as
 *  `Light_...fit`, not into a nested folder on the user's disk). */
export const downloadSingleFrame = async (
  collectionId: string,
  name: string,
  opts: FetchOpts,
): Promise<void> => {
  const resp = await presignDownload(collectionId, [name], opts);
  const url = resp.files[0]?.url;
  if (!url) throw new Error('no signed URL returned');
  const a = document.createElement('a');
  a.href = url;
  a.download = name.split('/').pop() || name;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/** GET /collections/{id}/files/{filename} — one file with signed URLs.
 *  Called by the lightbox on open to get the preview JPEG URL without
 *  paying signing latency on the whole list endpoint. */
export const getCollectionFile = (
  id: string,
  filename: string,
  opts: FetchOpts,
) =>
  request<CollectionFile>(
    'GET',
    `/collections/${encodeURIComponent(id)}/files/${encodeURIComponent(filename)}`,
    undefined,
    opts,
  );

export interface LogEvent {
  timestamp: number;   // ms since epoch
  message: string;
}

export interface LogsResponse {
  source: 'job' | 'cloudinit';
  hasStream: boolean;
  events: LogEvent[];
  nextForwardToken?: string;
  nextBackwardToken?: string;
}

/**
 * GET /logs/{id} — CloudWatch events for a job's stream.
 *
 * Pass `nextToken` from the previous response to forward-paginate; omit for
 * the initial fetch. `source` defaults to 'job' (siril-job.log).
 *
 * Selection knobs (all optional):
 *   startFromHead  false = return the tail (last `limit` events) — what you
 *                  want on the *first* fetch so the user isn't reading boot
 *                  chatter on a long-running job. Server default is true.
 *   startTime      ms epoch, inclusive lower bound on event timestamp.
 *   endTime        ms epoch, exclusive upper bound.
 */
// ---- Personal access tokens (PATs) ----
//
// Per-user API credentials for CLI / script access. Mint returns the raw
// token string exactly once; list and revoke only ever see the display
// prefix. Revoke is idempotent — the response shape is the same whether
// the token existed and was deleted or never existed at all (prevents
// enumeration of another user's tokenIds).

export interface PatSummary {
  tokenId: string;
  name: string;
  prefix: string;          // e.g. "pk_live_a3f2" — safe to display
  createdAt: string;       // ISO
  lastUsedAt?: string;     // ISO; absent = never used since mint
}

export interface MintPatResp extends PatSummary {
  // Raw token string. Present ONLY on the mint response — the whole point
  // of a PAT is that this string never leaves the user's hands. UI must
  // show once, hide behind a copy-to-clipboard affordance, and forget.
  token: string;
}

/** POST /pats — mint a new personal access token. */
export const mintPat = (name: string, opts: FetchOpts) =>
  request<MintPatResp>('POST', '/pats', { name }, opts);

/** GET /pats — list caller's PATs, newest first. */
export const listPats = (opts: FetchOpts) =>
  request<{ pats: PatSummary[] }>('GET', '/pats', undefined, opts);

/** DELETE /pats/{id} — revoke by tokenId. Idempotent. */
export const revokePat = (tokenId: string, opts: FetchOpts) =>
  request<{ tokenId: string; revoked: boolean }>(
    'DELETE', `/pats/${encodeURIComponent(tokenId)}`, undefined, opts,
  );

/** GET /admin/capacity — spot capacity + price picture for our three
 *  launch tiers. Admin/beta only (backend returns 403 otherwise).
 *  Mirror of the Go structs in lambda-start-job/admin_capacity.go — any
 *  field added there needs the same field here. */
export interface CapacityAZPrice {
  az: string;
  usdHr: number;
}
export interface CapacityHistoryPoint {
  t: string;
  usdHr: number;
}
export interface CapacityInstance {
  type: string;
  vcpu: number;
  ramGB: number;
  nvmeGB: number;
  recommendedFor?: string;
  current: CapacityAZPrice[];
  latestUsdHr: number;
  sevenDayAvg: number;
  sevenDayMin: number;
  sevenDayMax: number;
  sampleCount: number;
  history: CapacityHistoryPoint[];
}
export interface CapacityPlacement {
  az: string;
  score: number;
}
export interface CapacityTier {
  id: 'small' | 'default' | 'big';
  label: string;
  description: string;
  triggerRule: string;
  instances: CapacityInstance[];
  placementScores: CapacityPlacement[];
}
export interface CapacityResponse {
  tiers: CapacityTier[];
  updatedAt: string;
  region: string;
  cachedForSeconds: number;
}

export const getAdminCapacity = (opts: FetchOpts) =>
  request<CapacityResponse>('GET', '/admin/capacity', undefined, opts);

export const getLogs = (
  id: string,
  opts: FetchOpts & {
    source?: 'job' | 'cloudinit';
    nextToken?: string;
    limit?: number;
    startFromHead?: boolean;
    startTime?: number;
    endTime?: number;
  } = {},
) => {
  const qs = new URLSearchParams();
  if (opts.source)                   qs.set('source',        opts.source);
  if (opts.nextToken)                qs.set('nextToken',     opts.nextToken);
  if (opts.limit)                    qs.set('limit',         String(opts.limit));
  if (opts.startFromHead !== undefined) qs.set('startFromHead', String(opts.startFromHead));
  if (opts.startTime)                qs.set('startTime',     String(opts.startTime));
  if (opts.endTime)                  qs.set('endTime',       String(opts.endTime));
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<LogsResponse>(
    'GET', `/logs/${encodeURIComponent(id)}${suffix}`, undefined, opts,
  );
};
